import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useNavigationGuard } from '../context/NavigationContext'
import { useLang } from '../context/LangContext'
import { authedFetch } from '../utils/shopifyFetch'
import { isAdminView } from '../utils/adminSession'
import { withoutOrders } from '../lib/orderSection'

function timeAgo(iso) {
  if (!iso) return null
  const s = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.floor(s/60)}m ago`
  if (s < 86400) return `${Math.floor(s/3600)}h ago`
  return `${Math.floor(s/86400)}d ago`
}

export default function Sidebar({ shop, open = false, closed = false, onNavigate }) {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { authorize } = useNavigationGuard()
  const { t } = useLang()
  const [lastSync, setLastSync] = useState(null)
  // Merchant-facing store name; the domain is the fallback
  const [shopName, setShopName] = useState('')
  // Set per shop by support. Until the settings read answers, the page is
  // shown — a nav entry that appears a moment late is better than one that
  // vanishes in front of the seller.
  const [hidePricing, setHidePricing] = useState(false)
  // Which side is the hub. Null until the settings read answers, and the
  // hubOnly filter below treats null as "show" for the same reason
  // hidePricing does: an entry that appears a moment late is better than one
  // that vanishes in front of whoever is looking at it.
  const [hub, setHub] = useState(null)
  // The custom-app shop keeps Orders whatever its hub — see lib/orderSection.
  const [customApp, setCustomApp] = useState(false)

  const NAV_ITEMS = [
    // Dashboard and Templates are reachable but not listed. The dashboard is
    // the app root and where the upload flow starts, so the route stays; the
    // templates live inside a listing rule now.
    //
    // Except in the admin view, where leaving it unlisted strands you. A
    // merchant always has a way back to the app root without this entry:
    // Shopify draws the app's nav from App Bridge, and the app name in the
    // admin sidebar is itself a link to the root. The admin view has neither.
    // App Bridge is deliberately not loaded for an impersonation — with it,
    // it sends the whole tab into the Shopify admin, which staff have no
    // access to — so this sidebar is the only nav there is, and without a
    // Dashboard entry there is no route back to the dashboard at all once
    // you have navigated away from it.
    //
    // Hidden for everyone else, so the merchant's nav is exactly as it was.
    // '/dashboard' rather than '/', which renders the same page: it is the
    // path the admin tab is opened on, so the active highlight matches, and
    // '/' at the server is the install route's.
    { path:'/dashboard', labelKey:'nav_dashboard', icon:'▦', adminOnly:true },
    { path:'/products',  labelKey:'nav_products',  icon:'≡' },
    // Dropped by withoutOrders below for an eBay-hub shop, and kept for the
    // shopify and two_way shops that still have the section. Not expressed as
    // `hubOnly` because that field means "only under the Shopify hub", and
    // this is the opposite question: everything EXCEPT the eBay hub.
    { path:'/orders',    labelKey:'nav_orders',     icon:'⊟' },
    // `hubOnly`, matching ShopifyNavMenu. A listing rule decides the category,
    // price and policies a product is published to eBay under; with eBay as the
    // hub nothing is published from here, so the section has nothing to decide.
    //
    // The rule lived only in the Shopify nav, which is what Shopify draws from
    // App Bridge. This sidebar is what the app draws itself — and it is the ONLY
    // nav in the admin panel, where App Bridge never loads — so viewing a seller
    // from admin showed them a Listing Rules entry their own app hides.
    { path:'/profiling', labelKey:'nav_profiling',  icon:'◉', hubOnly:'shopify' },
    { path:'/activity',  labelKey:'nav_activity',   icon:'▣' },
    { path:'/pricing',   labelKey:'nav_pricing',    icon:'◈', hideable:'pricing' },
    { path:'/settings',  labelKey:'nav_settings',   icon:'⚙' },
    // { path:'/help',      labelKey:'nav_help',       icon:'?' },
  ]

  useEffect(() => {
    if (!shop) return
    const load = () => {
      authedFetch(`/api/settings?shop=${shop}`)
        .then(r => r.json())
        .then(d => {
          const t = d?.sync?.lastShopifySync
          if (t) setLastSync(new Date(t))
          if (d?.shopify?.name) setShopName(d.shopify.name)
          setHidePricing(d?.sync?.hidePricing === true)
          setHub(d?.sync?.syncSource || 'two_way')
          setCustomApp(d?.shopify?.customApp === true)
        })
        .catch(() => {})
    }
    load()
    const interval = setInterval(load, 60000)
    return () => clearInterval(interval)
  }, [shop])

  const syncLabel = lastSync ? timeAgo(lastSync.toISOString()) : 'Never'

  return (
    // Positioning lives in .sidebar so the responsive drawer rules can
    // override it; inline styles would always win.
    // `inert` keeps the closed drawer out of the tab order — translating it
    // off-screen hides it visually but leaves it keyboard-focusable.
    <aside className={`sidebar${open ? ' is-open' : ''}`} inert={closed ? '' : undefined}>
      {/* Logo */}
      <div style={{ padding:'18px 20px 14px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,var(--sh),var(--eb))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>S↔E</div>
        <div>
          <div style={{ fontSize:15, fontWeight:600, letterSpacing:'-.3px' }}>SyncE Bridge</div>
          <div style={{ fontSize:10, color:'var(--text3)', fontFamily:'var(--mono)' }}>Shopify ↔ eBay</div>
        </div>
      </div>

      {/* Nav */}
      <nav id="main-nav" aria-label="Main navigation" style={{ padding:'14px 12px 4px', flex:1, overflowY:'auto' }}>
        <div style={{ fontSize:10, fontWeight:600, color:'var(--text3)', letterSpacing:'.08em', textTransform:'uppercase', padding:'0 8px', marginBottom:4 }}>{t('menu')}</div>
        {/* Orders is hidden for the eBay hub only — `hub` is the same value
            the hubOnly filter below reads, and it is null until settings load,
            which shows the entry rather than hiding it. */}
        {withoutOrders(NAV_ITEMS, hub, customApp)
          .filter(i => !(i.hideable === 'pricing' && hidePricing))
          // Admin-only entries appear only in the admin view. Nothing is added
          // to what a seller sees.
          // isAdminView, not isAdminImpersonation: the latter reads the url
          // alone, and the parameter is stripped by the first navigation — so
          // the entry appeared on the dashboard and then vanished the moment
          // it was used, which is precisely when it is needed.
          .filter(i => !i.adminOnly || isAdminView())
          // `hubOnly` was declared on an entry here but never actually applied
          // — the only filter was the pricing one — so an eBay-hub seller's
          // portal still listed Listing Rules, which their own app hides.
          // Same test ShopifyNavMenu uses, so the two navs agree.
          .filter(({ hubOnly }) => !(hubOnly === 'shopify' && hub && hub !== 'shopify'))
          .map(item => {
          const active = location.pathname === item.path
          return (
            <button
              key={item.path}
              onClick={() => { authorize(); navigate(item.path); onNavigate?.() }}
              aria-current={active ? 'page' : undefined}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 10px', borderRadius:'var(--radius)', cursor:'pointer', marginBottom:2, width:'100%', textAlign:'left',
                color:active?'var(--text)':'var(--text2)', background:active?'var(--bg3)':'transparent',
                border:active?'1px solid var(--border)':'1px solid transparent', position:'relative', transition:'all .15s' }}>
              {active && <div style={{ position:'absolute', left:-12, top:'50%', transform:'translateY(-50%)', width:3, height:20, background:'var(--accent)', borderRadius:'0 3px 3px 0' }}/>}
              <span style={{ fontSize:16, opacity:active?1:.7, width:18, textAlign:'center' }} aria-hidden="true">{item.icon}</span>
              <span style={{ fontSize:13.5, fontWeight:500 }}>{t(item.labelKey)}</span>
            </button>
          )
        })}
      </nav>

      {/* Sync status */}
      <div style={{ padding:12, borderTop:'1px solid var(--border)' }}>
        <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 12px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background: lastSync ? 'var(--green)' : 'var(--text3)', boxShadow: lastSync ? '0 0 8px var(--green)' : 'none', flexShrink:0 }}/>
            <span style={{ fontSize:12, fontWeight:600, color: lastSync ? 'var(--green)' : 'var(--text3)' }}>
              {lastSync ? t('shopify_synced') : t('not_synced')}
            </span>
          </div>
          <div style={{ fontSize:11, color:'var(--text3)' }}>
            {t('last_sync')}: <span style={{ fontFamily:'var(--mono)' }}>{syncLabel}</span>
          </div>
          {shop && (
            <div title={shop} style={{ marginTop:4, overflow:'hidden' }}>
              {shopName && (
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{shopName}</div>
              )}
              <div style={{ fontSize:10, color:'var(--text3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{shop}</div>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
